<template>
  <div class="profile-main-wrapper">
    <!-- Левая колонка (Аватар + ID) -->
    <div class="profile-avatar-section">
      <v-card class="profile-card pa-5" elevation="2">
        <v-avatar size="120">
          <v-img src="@/assets/profile-avatar.svg" />
        </v-avatar>
        <v-btn class="edit-icon" icon>
          <v-icon color="white">mdi-pencil</v-icon>
        </v-btn>
        <h3 class="mt-3">{{ form.name }}</h3>
        <p class="grey--text">id: {{ form.id }}</p>
        <v-btn
          class="profile-exit-btn red--text"
          outlined
          @click="logOut"
        >
          {{ $t("landing.exitBtn") }}
        </v-btn>
      </v-card>
    </div>

    <!-- Правая колонка (Форма) -->
    <div class="profile-info-section">
      <ProfileForm
        v-if="!isLoading"
        ref="profileForm"
        :form-data="form"
        :doc-type-items="docType"
        :issued-by-items="issuedBy"
        :loading="isSubmitLoading"
        :is-submit-loading="isSubmitLoading"
        @submit="handleSubmitForm"
        @update:form-data="form = $event"
        @validation-error="handleValidationError"
      />
    </div>

    <ProgressCircular
      :isSubmitLoading="isSubmitLoading"
      v-if="isLoading || isSubmitLoading"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { showToast } from "@/utils/toast";
import ProgressCircular from "@/components/custom/ProgressCircular.vue";
import ProfileForm from "@/components/profile/ProfileForm.vue";
import { saveProfile } from "@/api/profile";

export default {
  name: "ProfileMain",
  components: { 
    ProgressCircular,
    ProfileForm
  },

  data() {
    return {
      isLoading: false,
      isSubmitLoading: false,
      form: {
        name: "",
        docType: "",
        email: "",
        docNumber: "",
        workplace: "",
        issuedBy: "",
        phone: "",
        issueDate: "",
        additionalPhone: "",
      },
    };
  },
  
  created() {
    this.initializePageData();
  },
  
  mounted() {
    // Инициализация формы из Vuex
    this.initializeFormFromUser();
  },
  
  methods: {
    ...mapActions({
      fetchProfileData: "profileData/loadProfileData",
      fetchIssuedByData: "issuedBy/loadIssuedByData",
      fetchDocumentTypeData: "documentType/loadDocumentTypeData",
    }),
    
    async initializePageData() {
      if (this.isLoading) return;

      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchProfileData(),
          this.fetchIssuedByData(),
          this.fetchDocumentTypeData(),
        ]);
        this.initializeFormFromUser();
      } catch (error) {
        showToast(this, {
          text: `${error.response.data.message || error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    initializeFormFromUser() {
      if (this.user) {
        // Форматируем телефонные номера для отображения
        this.form = {
          ...this.user,
          phone: this.formatPhoneNumber(this.user.phone),
          additionalPhone: this.user.additionalPhone ? 
            this.formatPhoneNumber(this.user.additionalPhone) : 
            "",
        };
      }
    },
    
    // Метод formatPhoneNumber из RegistrationStepper
    formatPhoneNumber(number) {
      if (!number) return "";
      
      // Преобразуем номер в строку и удаляем все нецифровые символы
      let clean = number.toString().replace(/\D/g, "");

      // Нормализация номера
      if (clean.startsWith("8")) {
        clean = "7" + clean.slice(1);
      } else if (clean.startsWith("07")) {
        clean = "7" + clean.slice(1);
      } else if (clean.length === 10 && !clean.startsWith("7")) {
        clean = "7" + clean;
      }

      // Форматируем в маску +7(XXX) XXX-XX-XX
      if (clean.length === 11 && clean.startsWith("7")) {
        return clean.replace(
          /^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/,
          "+$1($2) $3-$4-$5"
        );
      }
      
      // Возвращаем оригинальный номер, если формат не распознан
      return number;
    },
    
    // Метод normalizePhone из RegistrationStepper
    normalizePhone(phone) {
      if (!phone) return '';
      // Убираем все нецифровые символы
      const digits = phone.toString().replace(/\D/g, '');
      
      // Если номер начинается с 8, заменяем на 7
      if (digits.startsWith('8')) {
        return '7' + digits.slice(1);
      }
      
      // Если номер начинается с 7 и имеет 11 цифр, возвращаем как есть
      if (digits.startsWith('7') && digits.length === 11) {
        return digits;
      }
      
      // Если номер 10 цифр, добавляем 7 в начало
      if (digits.length === 10) {
        return '7' + digits;
      }
      
      return digits;
    },
    
    // Метод handleSubmitForm из RegistrationStepper (адаптированный)
    async handleSubmitForm(submissionData) {
      this.isSubmitLoading = true;
      
      try {
        const payload = {
          ...submissionData,
          phone: this.normalizePhone(submissionData.phone),
          additionalPhone: submissionData.additionalPhone ? 
            this.normalizePhone(submissionData.additionalPhone) : 
            null,
        };
        delete payload.agreement; // Удаляем согласие, если оно есть
        await saveProfile(payload);
        
        showToast(this, {
          text: this.$t(`toastText.successfullySaved`),
          status: "success",
          duration: 3000,
        });
        
        // Обновляем данные пользователя после успешного сохранения
        await this.fetchProfileData();
        
      } catch (error) {
        
        let errorMessage = this.$t(`toastText.saveError`);
        
        if (error.response) {
          
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else {
            switch (error.response.status) {
              case 400:
                errorMessage = this.$t(`toastText.validationError`);
                break;
              case 409:
                errorMessage = this.$t(`toastText.userExists`);
                break;
              case 500:
                errorMessage = this.$t(`toastText.serverError`);
                break;
            }
          }
        }
        
        showToast(this, {
          text: errorMessage,
          status: "error",
          duration: 5000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    },
    
    // Обработчик ошибок валидации
    handleValidationError(errorData) {
      console.warn("Validation error:", errorData);
      showToast(this, {
        text: this.$t(`toastText.fields`),
        status: "warning",
        duration: 3000,
      });
    },

    logOut() {
      this.isLoading = true;
      this.$store.commit("auth/LOGOUT");

      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
  },
  
  computed: {
    ...mapGetters("profileData", ["user"]),
    ...mapGetters("issuedBy", ["issuedBy"]),
    ...mapGetters("documentType", ["docType"]),
  },
  
  watch: {
    user: {
      handler(val) {
        if (val) {
          this.initializeFormFromUser();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Стили остаются без изменений */
.profile-main-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
}
.profile-card {
  text-align: center;
  width: 270px;
  position: relative;
  height: fit-content;
  padding: 0;
}
.profile-avatar-section {
  flex: 1;
  max-width: 270px;
}
.profile-info-section {
  flex: 2;
  min-width: 300px;
  max-width: 950px;
}
.profile-card-info {
  width: 950px;
  padding: 40px !important;
}
.edit-icon {
  position: absolute;
  top: 105px;
  right: 70px;
  background: #5e5af9;
}

.v-btn.profile-cancel-btn {
  background-color: #7e7e7e;
  margin-right: 8px;
  text-transform: none !important;
  border-radius: 8px;
}
.v-btn.profile-save-btn {
  background-color: #5e5af9 !important;
  color: #ffffff;
  border-radius: 8px;
  text-transform: none !important;
}
.profile-actions {
  display: flex;
  justify-content: end;
}

@media (max-width: 1024px) {
  .profile-main-wrapper {
    flex-direction: column;
    align-items: center;
  }
  .profile-info-section {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .profile-card-info {
    padding: 20px;
  }
  .profile-info-section {
    padding: 0 25px;
  }
  .profile-actions {
    justify-content: center;
  }
  .profile-forms-row {
    flex-direction: column;
  }
  .profile-form-field {
    max-width: 100%;
  }
  .v-btn.profile-save-btn {
    padding: 25px 20px;
    margin: 0px auto;
    margin-top: 20px;
  }
}

@media (max-width: 640px) {
  .profile-actions {
    flex-direction: column;
    gap: 10px;
  }
  .v-btn.profile-cancel-btn {
    margin-right: unset;
  }
  .profile-card-info h2{
    text-align: center;
  }
}
</style>